import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import { API_BASE_URL } from '../../../config/apiEndpoints';
import { resolveBookingPaymentContext, buildApprovalEmailParams } from '../../../utils/bookingPayment';
import { touchVenueDashboardActivity } from '../../../utils/venueDashboardSession';
import {
  fetchVenueBookingsList,
  fetchVenueManagePastSeed,
  fetchVenuePastBookingsPage,
  VENUE_BOOKINGS_PAST_PAGE_LIMIT,
  VENUE_BOOKINGS_RECENT_PAST_DAYS
} from '../utils/venueBookingsListFetch';
import {
  formatLocalYmd,
  addDaysToYmd,
  mergeVenueBookingLists,
  normalizeVenueBookingsListRows,
  getBookingsByDateIndexCached
} from '../utils/venueBookingListModel';
import { getBookingSummaryCounts } from '../utils/bookingUtils';
import {
  getVenueBookingsCacheEntry,
  setVenueBookingsCacheEntry,
  patchVenueBookingsCacheBookings,
  isVenueBookingsCacheFresh
} from '../utils/venueBookingsCache';

export const useVenueBookings = (venueId) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPast, setLoadingPast] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [manageSeedLoaded, setManageSeedLoaded] = useState(false);
  const pastOffsetRef = useRef(0);
  const pastCutoffYmdRef = useRef(null);
  const manageSeedInflightRef = useRef(false);

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      'Content-Type': 'application/json'
    }),
    []
  );

  const hydrateFromCache = useCallback(() => {
    const entry = getVenueBookingsCacheEntry(venueId);
    if (!entry?.bookings?.length) return false;
    setBookings(entry.bookings);
    pastOffsetRef.current = entry.pastOffset ?? 0;
    setHasMorePast(entry.hasMorePast ?? true);
    setManageSeedLoaded(Boolean(entry.manageSeedLoaded));
    if (entry.pastCutoffYmd) pastCutoffYmdRef.current = entry.pastCutoffYmd;
    return true;
  }, [venueId]);

  const fetchVenueBookings = useCallback(async (options = {}) => {
    const { silent = false, force = false } = options;
    if (!venueId) return;

    const todayYmd = formatLocalYmd(new Date());
    if (!force && isVenueBookingsCacheFresh(venueId)) {
      hydrateFromCache();
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);
      touchVenueDashboardActivity();

      pastCutoffYmdRef.current = addDaysToYmd(todayYmd, -(VENUE_BOOKINGS_RECENT_PAST_DAYS + 1));
      if (force) {
        pastOffsetRef.current = 0;
        setHasMorePast(true);
        setManageSeedLoaded(false);
      }

      const rows = await fetchVenueBookingsList(venueId, authHeaders(), { todayYmd });
      const normalized = normalizeVenueBookingsListRows(mergeVenueBookingLists([], rows));
      setBookings(normalized);
      setVenueBookingsCacheEntry(venueId, {
        bookings: normalized,
        pastOffset: pastOffsetRef.current,
        hasMorePast: true,
        pastCutoffYmd: pastCutoffYmdRef.current,
        manageSeedLoaded: false
      });
    } catch (error) {
      if (silent) return;
      if (error.message.includes('404')) {
        setError(null);
        setBookings([]);
      } else {
        const msg = error.message || '';
        if (msg.includes('401') || msg.includes('403')) {
          setError('You are not authorised to view bookings. Please log in again.');
        } else if (msg.includes('500') || msg.includes('502') || msg.includes('503')) {
          setError('Unable to load bookings. Please try again in a moment.');
        } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
          setError('Unable to reach the server. Please check your connection.');
        } else {
          setError('Failed to load bookings. Please refresh the page.');
        }
        setBookings([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [venueId, authHeaders, hydrateFromCache]);

  const ensureManageBookingsWindow = useCallback(async () => {
    if (!venueId || manageSeedLoaded || manageSeedInflightRef.current) return { added: 0 };
    manageSeedInflightRef.current = true;
    try {
      const todayYmd = formatLocalYmd(new Date());
      if (!pastCutoffYmdRef.current) {
        pastCutoffYmdRef.current = addDaysToYmd(todayYmd, -(VENUE_BOOKINGS_RECENT_PAST_DAYS + 1));
      }
      const page = await fetchVenueManagePastSeed(venueId, authHeaders(), { todayYmd });
      const normalized = normalizeVenueBookingsListRows(page);
      setManageSeedLoaded(true);
      setBookings((prev) => {
        const merged = mergeVenueBookingLists(prev, normalized);
        setVenueBookingsCacheEntry(venueId, {
          bookings: merged,
          pastOffset: pastOffsetRef.current,
          hasMorePast,
          pastCutoffYmd: pastCutoffYmdRef.current,
          manageSeedLoaded: true
        });
        return merged;
      });
      return { added: normalized.length };
    } catch {
      return { added: 0 };
    } finally {
      manageSeedInflightRef.current = false;
    }
  }, [venueId, authHeaders, manageSeedLoaded, hasMorePast]);

  const loadMorePastBookings = useCallback(async () => {
    if (!venueId || loadingPast || !hasMorePast) return { added: 0 };
    const beforeYmd = pastCutoffYmdRef.current;
    if (!beforeYmd) return { added: 0 };

    setLoadingPast(true);
    try {
      touchVenueDashboardActivity();
      const page = await fetchVenuePastBookingsPage(venueId, authHeaders(), {
        beforeYmd,
        offset: pastOffsetRef.current
      });
      const normalized = normalizeVenueBookingsListRows(page);
      if (normalized.length < VENUE_BOOKINGS_PAST_PAGE_LIMIT) {
        setHasMorePast(false);
      }
      pastOffsetRef.current += normalized.length;
      setBookings((prev) => {
        const merged = mergeVenueBookingLists(prev, normalized);
        setVenueBookingsCacheEntry(venueId, {
          bookings: merged,
          pastOffset: pastOffsetRef.current,
          hasMorePast: normalized.length >= VENUE_BOOKINGS_PAST_PAGE_LIMIT,
          pastCutoffYmd: pastCutoffYmdRef.current,
          manageSeedLoaded: true
        });
        return merged;
      });
      return { added: normalized.length };
    } catch {
      return { added: 0 };
    } finally {
      setLoadingPast(false);
    }
  }, [venueId, authHeaders, loadingPast, hasMorePast]);

  const updateBookingStatus = async (
    bookingId,
    statusAction,
    _customerMessage,
    paymentStatusAction,
    bookingOverride = null
  ) => {
    if (!bookingId) {
      return {
        ok: false,
        emailSent: false,
        emailError: 'Booking ID is required.',
        errorMessage: 'Booking ID is required to update status.'
      };
    }
    if (!statusAction && !paymentStatusAction) {
      return { ok: false, emailSent: false, emailError: null };
    }

    const matchedBooking =
      bookingOverride ||
      bookings.find(
        (b) =>
          String(b.id) === String(bookingId) ||
          String(b.booking_reference) === String(bookingId)
      );
    const apiBookingId = matchedBooking?.id ?? matchedBooking?.booking_id ?? bookingId;
    const linkBookingId = matchedBooking?.id ?? matchedBooking?.booking_id ?? apiBookingId;

    try {
      const payload = {};
      let apiStatus = null;
      let apiPaymentStatus = null;
      let emailSent = false;
      let emailError = null;
      let paymentMode = null;

      const sendApprovalEmail = async () => {
        let bookingForEmail = matchedBooking;
        try {
          const fresh = await venueDashboardApiService.getBookingById(apiBookingId);
          if (fresh && typeof fresh === 'object') {
            bookingForEmail = { ...matchedBooking, ...fresh, list_summary: false };
          }
        } catch {
          // Fall back to the in-memory row if the full fetch fails.
        }
        const paymentContext = await resolveBookingPaymentContext(linkBookingId, venueId, null, {
          booking: bookingForEmail
        });
        await venueDashboardApiService.sendBookingApprovedEmail(
          apiBookingId,
          buildApprovalEmailParams(paymentContext)
        );
        paymentMode = paymentContext.mode;
        emailSent = true;
      };

      if (statusAction) {
        apiStatus = statusAction === 'on_hold' ? 'pending' : statusAction;
        payload.status = apiStatus;
      }
      if (paymentStatusAction) {
        payload.payment_status = paymentStatusAction === 'completed' ? 'paid' : paymentStatusAction;
        apiPaymentStatus = payload.payment_status;
      }

      const url = `${API_BASE_URL}/bookings/${encodeURIComponent(String(apiBookingId))}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseBody = await response.text();

      if (!response.ok) {
        if (response.status === 404) {
          setBookings((prevBookings) => {
            const next = prevBookings.map((booking) =>
              booking.id === bookingId || booking.booking_reference === bookingId
                ? {
                    ...booking,
                    ...(apiStatus && { status: apiStatus }),
                    ...(apiPaymentStatus && { payment_status: apiPaymentStatus })
                  }
                : booking
            );
            patchVenueBookingsCacheBookings(venueId, next);
            return next;
          });
          return { ok: true, emailSent: false, emailError: null, paymentMode: null };
        }
        console.error('Booking status update failed', response.status, responseBody);
        if (responseBody.includes('bookings_date_check')) {
          throw new Error('BOOKINGS_DATE_CHECK');
        }
        throw new Error(`HTTP_${response.status}`);
      }

      if (apiStatus === 'confirmed') {
        try {
          await sendApprovalEmail();
        } catch (emailErr) {
          emailError = emailErr?.message || 'Could not send approval email with payment link.';
        }
      } else if (apiStatus === 'canceled_by_owner') {
        try {
          await venueDashboardApiService.sendBookingCancelledEmail(apiBookingId, {
            message: _customerMessage || '',
            reason: _customerMessage || 'Cancelled by venue',
            cancelled_by: 'owner'
          });
          emailSent = true;
        } catch (emailErr) {
          emailError =
            emailErr?.message || 'Booking was cancelled, but the notification email could not be sent.';
        }
      }

      setBookings((prevBookings) => {
        const next = prevBookings.map((booking) =>
          booking.id === bookingId ||
          booking.booking_reference === bookingId ||
          booking.id === apiBookingId ||
          booking.booking_reference === apiBookingId
            ? {
                ...booking,
                ...(apiStatus && { status: apiStatus }),
                ...(apiPaymentStatus && { payment_status: apiPaymentStatus })
              }
            : booking
        );
        patchVenueBookingsCacheBookings(venueId, next);
        return next;
      });
      setError(null);
      return { ok: true, emailSent, emailError, paymentMode };
    } catch (error) {
      const msg = error.message || '';
      let errorMessage = 'Failed to update booking. Please try again.';
      if (msg.includes('BOOKINGS_DATE_CHECK')) {
        errorMessage = "This booking's event date has passed, so its status can't be changed.";
      } else if (msg.includes('401') || msg.includes('403')) {
        errorMessage = 'You are not authorised to update this booking.';
      } else if (msg.includes('404')) {
        errorMessage = 'Booking not found.';
      } else if (msg.includes('500') || msg.includes('502') || msg.includes('503')) {
        errorMessage = 'Unable to update booking. Please try again in a moment.';
      } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        errorMessage = 'Unable to reach the server. Please check your connection.';
      }
      return { ok: false, emailSent: false, emailError: null, errorMessage };
    }
  };

  const updateBookingLocal = (bookingId, patchOrUpdater) => {
    if (!bookingId) return;
    setBookings((prevBookings) => {
      const next = prevBookings.map((booking) => {
        const isMatch =
          String(booking.id) === String(bookingId) ||
          String(booking.booking_reference) === String(bookingId);
        if (!isMatch) return booking;
        const patch =
          typeof patchOrUpdater === 'function' ? patchOrUpdater(booking) : patchOrUpdater || {};
        const merged = { ...booking, ...patch };
        if (patch.requirements || patch.packages) {
          merged.list_summary = false;
        }
        return merged;
      });
      patchVenueBookingsCacheBookings(venueId, next);
      return next;
    });
  };

  const replaceBookingInList = (fullBooking) => {
    if (!fullBooking?.id) return;
    setBookings((prev) => {
      const next = mergeVenueBookingLists(
        prev.filter((b) => String(b.id) !== String(fullBooking.id)),
        [{ ...fullBooking, list_summary: false }]
      );
      patchVenueBookingsCacheBookings(venueId, next);
      return next;
    });
  };

  const addBookingLocal = (booking) => {
    if (!booking) return;
    const ref = booking.booking_reference ?? booking.id;
    if (!ref) return;
    setBookings((prevBookings) => {
      const exists = prevBookings.some(
        (b) =>
          String(b.id) === String(ref) ||
          String(b.booking_reference) === String(ref) ||
          (booking.id != null &&
            (String(b.id) === String(booking.id) ||
              String(b.booking_reference) === String(booking.id)))
      );
      if (exists) return prevBookings;
      const next = mergeVenueBookingLists(prevBookings, [{ ...booking, list_summary: false }]);
      patchVenueBookingsCacheBookings(venueId, next);
      return next;
    });
  };

  useEffect(() => {
    if (!venueId) return;
    const hadCache = hydrateFromCache();
    fetchVenueBookings({ silent: hadCache, force: false });
  }, [venueId, fetchVenueBookings, hydrateFromCache]);

  useEffect(() => {
    if (!venueId || manageSeedLoaded || loading) return undefined;
    const schedule =
      typeof requestIdleCallback === 'function'
        ? (fn) => requestIdleCallback(fn, { timeout: 4000 })
        : (fn) => window.setTimeout(fn, 1500);
    const cancel =
      typeof requestIdleCallback === 'function'
        ? (id) => cancelIdleCallback(id)
        : (id) => window.clearTimeout(id);
    const idleId = schedule(() => {
      void ensureManageBookingsWindow();
    });
    return () => cancel(idleId);
  }, [venueId, manageSeedLoaded, loading, ensureManageBookingsWindow]);

  const bookingsByDateIndex = useMemo(
    () => getBookingsByDateIndexCached(venueId, bookings),
    [venueId, bookings]
  );

  const bookingSummaryCounts = useMemo(() => getBookingSummaryCounts(bookings), [bookings]);

  return {
    bookings,
    bookingsByDateIndex,
    bookingSummaryCounts,
    loading,
    error,
    loadingPast,
    hasMorePast,
    fetchVenueBookings,
    ensureManageBookingsWindow,
    loadMorePastBookings,
    replaceBookingInList,
    updateBookingStatus,
    updateBookingLocal,
    addBookingLocal
  };
};